/**
 * Market page — ranked by 1-month forecast.
 *
 * Stocks are ordered best-first by their confidence-weighted expected return,
 * so the strongest predicted movers sit at the top. The data-loading tools
 * (add a ticker, API key, CSV import, diagnostics) live in a single
 * collapsible "Add stocks & data setup" section so they don't clutter the
 * ranked view once things are loaded.
 */

import React, { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { apiClient } from "@/api/client";
import type { MarketOverview, MarketOverviewRow } from "@/types";
import SyncTickerPanel from "@/components/research/SyncTickerPanel";
import ImportPricesPanel from "@/components/research/ImportPricesPanel";
import ApiKeyPanel from "@/components/research/ApiKeyPanel";
import MarketFilterBar, { applyFilters, DEFAULT_FILTERS, type MarketFilters } from "@/components/research/MarketFilterBar";

const fmtPrice = (v: number | null): string =>
  v === null || v === undefined ? "—" : `$${v.toFixed(2)}`;
const fmtPct = (v: number | null): string =>
  v === null || v === undefined ? "—" : `${v >= 0 ? "+" : ""}${v.toFixed(2)}%`;
const fmtRatio = (v: number | null): string =>
  v === null || v === undefined ? "—" : v.toFixed(1);

interface DiagnoseSource {
  provider: string;
  ok: boolean;
  rows: number;
  latest: string | null;
  detail: string | null;
  seconds: number;
}
interface DiagnoseResult {
  symbol: string;
  price_sources: DiagnoseSource[];
  summary: string;
}

export default function Market(): React.ReactElement {
  const [overview, setOverview] = useState<MarketOverview | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isLoadingMajors, setIsLoadingMajors] = useState(false);
  const [progress, setProgress] = useState<{ done: number; total: number; current: string; elapsed: number } | null>(null);
  const [scanDepth, setScanDepth] = useState(30);
  const [filters, setFilters] = useState<MarketFilters>(DEFAULT_FILTERS);
  const [statusMessage, setStatusMessage] = useState<string | null>(null);
  const [setupOpen, setSetupOpen] = useState(false);
  const [diagnosis, setDiagnosis] = useState<DiagnoseResult | null>(null);
  const [isDiagnosing, setIsDiagnosing] = useState(false);
  const [isRescanning, setIsRescanning] = useState(false);

  const load = useCallback(async (): Promise<void> => {
    setIsLoading(true);
    setError(null);
    try {
      const { data } = await apiClient.get<MarketOverview>("/providers/overview");
      setOverview(data);
    } catch (err) {
      const detail = (err as { response?: { data?: { detail?: string } } })?.response?.data?.detail;
      setError(detail ? String(detail) : "Couldn't load the market overview.");
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  const loadMajors = async (): Promise<void> => {
    setIsLoadingMajors(true);
    setStatusMessage(null);
    const started = Date.now();
    try {
      // Fetch the symbol list and sync them one at a time so we can show a real
      // progress bar with timing, rather than waiting on one long request.
      const { data } = await apiClient.get<{ symbols: string[] }>("/providers/popular-symbols");
      const symbols = (data.symbols ?? []).slice(0, scanDepth);
      setProgress({ done: 0, total: symbols.length, current: "", elapsed: 0 });

      let ok = 0;
      const notes: string[] = [];
      for (let i = 0; i < symbols.length; i += 1) {
        const symbol = symbols[i];
        setProgress({
          done: i,
          total: symbols.length,
          current: symbol,
          elapsed: Math.round((Date.now() - started) / 1000),
        });
        try {
          const res = await apiClient.post<{ prices?: { available?: boolean; note?: string } }>(
            `/providers/sync/${symbol}`,
          );
          if (res.data.prices?.available !== false) ok += 1;
          else if (res.data.prices?.note) notes.push(res.data.prices.note);
        } catch (err) {
          const detail = (err as { response?: { data?: { detail?: string } } })?.response?.data
            ?.detail;
          if (detail) notes.push(String(detail));
        }
        await load(); // refresh the table progressively as each stock lands
      }
      setProgress({
        done: symbols.length,
        total: symbols.length,
        current: "",
        elapsed: Math.round((Date.now() - started) / 1000),
      });
      const uniqueNotes = Array.from(new Set(notes));
      setStatusMessage(
        ok === 0 && uniqueNotes.length > 0
          ? `Loaded ${ok} of ${symbols.length}. ${uniqueNotes[0]}`
          : `Loaded ${ok} of ${symbols.length} stocks with prices.`,
      );
    } catch (err) {
      const detail = (err as { response?: { data?: { detail?: string } } })?.response?.data?.detail;
      setStatusMessage(detail ? String(detail) : "Couldn't load the major stocks.");
    } finally {
      setIsLoadingMajors(false);
      setTimeout(() => setProgress(null), 1500);
    }
  };

  const rescanPrices = async (): Promise<void> => {
    setIsRescanning(true);
    setStatusMessage(null);
    try {
      const { data } = await apiClient.post<{ message: string }>("/providers/rescan-prices");
      setStatusMessage(data.message);
      await load();
    } catch (err) {
      const detail = (err as { response?: { data?: { detail?: string } } })?.response?.data?.detail;
      setStatusMessage(detail ? String(detail) : "Couldn't rescan for prices.");
    } finally {
      setIsRescanning(false);
    }
  };

  const runDiagnosis = async (): Promise<void> => {
    setIsDiagnosing(true);
    setDiagnosis(null);
    try {
      const { data } = await apiClient.get<DiagnoseResult>("/providers/diagnose", {
        params: { symbol: "AAPL" },
      });
      setDiagnosis(data);
    } catch {
      setDiagnosis(null);
    } finally {
      setIsDiagnosing(false);
    }
  };

  const rows: MarketOverviewRow[] = overview?.companies ?? [];
  const hasStocks = rows.length > 0;
  const noPrices = hasStocks && (overview?.with_prices ?? 0) === 0;

  // Sectors present in the loaded data, for the sector dropdown.
  const sectors = Array.from(
    new Set(rows.map((r) => r.sector).filter((sc): sc is string => Boolean(sc))),
  ).sort();

  // Filtered + sorted view drives the table; the "not yet ranked" chips stay
  // as the stocks with no forecast yet (unaffected by sort, still respect the
  // budget/sector/fund filters so the whole page reflects the screen).
  const visible = applyFilters(rows, filters);
  const ranked = visible.filter((r) => r.outlook_1m);
  const unranked = visible.filter((r) => !r.outlook_1m);

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <h1 className="text-2xl font-semibold">Top predicted stocks</h1>
          <p className="mt-1 text-sm text-mist-400">
            Your loaded stocks, ranked best-first by projected 1-month return with a rise/fall
            estimate. Scan more names to widen the search for the best.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <select
            value={scanDepth}
            onChange={(e) => setScanDepth(Number(e.target.value))}
            disabled={isLoadingMajors}
            className="rounded-lg border border-ink-600 bg-ink-900 px-2 py-2 text-sm text-mist-200 focus:border-ink-500 focus:outline-none disabled:opacity-60"
            title="How many stocks to scan"
          >
            <option value={25}>Scan 25</option>
            <option value={50}>Scan 50</option>
            <option value={100}>Scan 100</option>
            <option value={250}>Scan 250</option>
            <option value={697}>Scan all 697</option>
          </select>
          <button
            type="button"
            onClick={() => void loadMajors()}
            disabled={isLoadingMajors}
            className="rounded-lg border border-signal-teal/40 bg-signal-teal/10 px-4 py-2 text-sm font-medium text-signal-teal transition hover:bg-signal-teal/20 disabled:opacity-60"
          >
            {isLoadingMajors ? "Scanning…" : "Scan for best"}
          </button>
        </div>
      </div>

      {/* Progress bar — always visible while loading, not buried in the setup panel */}
      {progress && (
        <div className="panel p-4">
          <div className="mb-1 flex justify-between text-xs text-mist-400">
            <span>
              {progress.done < progress.total
                ? `Loading ${progress.current}\u2026 (${progress.done}/${progress.total})`
                : `Done \u2014 ${progress.total} stocks in ${progress.elapsed}s`}
            </span>
            <span>{progress.elapsed}s</span>
          </div>
          <div className="h-2 w-full overflow-hidden rounded-full bg-ink-800">
            <div
              className="h-full bg-signal-teal transition-all duration-300"
              style={{ width: `${(progress.done / Math.max(progress.total, 1)) * 100}%` }}
            />
          </div>
        </div>
      )}

      {/* Collapsible setup — keeps the ranked view clean */}
      <div className="panel overflow-hidden">
        <button
          type="button"
          onClick={() => setSetupOpen((open) => !open)}
          className="flex w-full items-center justify-between px-5 py-3 text-left"
        >
          <span className="text-sm font-medium text-mist-100">Add stocks &amp; data setup</span>
          <span className="text-mist-400">{setupOpen ? "▲" : "▼"}</span>
        </button>
        {setupOpen && (
          <div className="space-y-4 border-t border-ink-700 p-5">
            <div className="flex flex-wrap gap-2">
              <button
                type="button"
                onClick={() => void runDiagnosis()}
                disabled={isDiagnosing}
                className="rounded-lg border border-ink-700 bg-ink-900 px-4 py-2 text-sm font-medium text-mist-300 transition hover:border-ink-500 disabled:opacity-60"
              >
                {isDiagnosing ? "Testing…" : "Test data sources"}
              </button>
              <button
                type="button"
                onClick={() => void rescanPrices()}
                disabled={isRescanning}
                title="Re-checks stocks already loaded that are missing prices — skips ones that already have them"
                className="rounded-lg border border-ink-700 bg-ink-900 px-4 py-2 text-sm font-medium text-mist-300 transition hover:border-ink-500 disabled:opacity-60"
              >
                {isRescanning ? "Rescanning…" : "Rescan for missing prices"}
              </button>
            </div>
            {statusMessage && <p className="text-sm text-mist-300">{statusMessage}</p>}
            {diagnosis && (
              <div className="rounded-lg border border-ink-700 bg-ink-900/60 p-4">
                <p className="text-sm text-mist-400">{diagnosis.summary}</p>
                <ul className="mt-2 space-y-1">
                  {diagnosis.price_sources.map((src) => (
                    <li key={src.provider} className="text-sm">
                      <span className={src.ok ? "text-emerald-400" : "text-rose-400"}>
                        {src.ok ? "✓" : "✗"}
                      </span>{" "}
                      <span className="text-mist-200">{src.provider}</span>{" "}
                      <span className="text-mist-400">
                        {src.ok ? `${src.rows} rows` : src.detail}
                      </span>
                    </li>
                  ))}
                </ul>
              </div>
            )}
            <SyncTickerPanel onSynced={() => void load()} />
            <ApiKeyPanel onSaved={() => void load()} />
            <ImportPricesPanel onImported={() => void load()} />
          </div>
        )}
      </div>

      {isLoading && <p className="text-sm text-mist-400">Loading…</p>}
      {error && <p className="text-sm text-rose-400">{error}</p>}

      {!isLoading && !hasStocks && (
        <div className="panel flex flex-col items-center justify-center gap-3 py-16 text-center">
          <span className="text-3xl">📈</span>
          <p className="text-sm font-medium text-mist-200">No stocks loaded yet</p>
          <p className="max-w-sm text-sm text-mist-400">
            Use the <span className="text-signal-teal">Load major stocks</span> button at the top
            right, or add a ticker in “Add stocks &amp; data setup”.
          </p>
        </div>
      )}

      {noPrices && (
        <div className="panel border-l-2 border-l-signal-amber p-5">
          <p className="text-sm font-medium text-mist-100">
            Stocks loaded, but rankings need prices
          </p>
          <p className="mt-1 text-sm text-mist-400">
            Financials came from SEC EDGAR, but the free price sources are blocking requests, so
            there's nothing to forecast from yet. Open “Add stocks &amp; data setup” above and add a
            free API key (fastest) or import a CSV. Rankings appear the moment prices load.
          </p>
        </div>
      )}

      {hasStocks && (
        <MarketFilterBar
          filters={filters}
          onChange={setFilters}
          sectors={sectors}
          resultCount={visible.length}
          totalCount={rows.length}
        />
      )}

      {hasStocks && !noPrices && ranked.length === 0 && unranked.length === 0 && (
        <p className="panel p-5 text-sm text-mist-400">
          No stocks match these filters. Try widening the price or P/E range, or reset the filters.
        </p>
      )}

      {ranked.length > 0 && (
        <div className="panel overflow-x-auto">
          <table className="w-full min-w-[1040px] text-left text-sm">
            <thead className="border-b border-ink-700 text-xs uppercase tracking-wide text-mist-400">
              <tr>
                <th className="px-3 py-3">#</th>
                <th className="px-3 py-3">Stock</th>
                <th className="px-3 py-3">Price</th>
                <th className="px-3 py-3">1-mo estimate</th>
                <th className="px-3 py-3">Expected price</th>
                <th className="px-3 py-3">1-mo range</th>
                <th className="px-3 py-3">Confidence</th>
                <th className="px-3 py-3">P/E</th>
                <th className="px-3 py-3">P/B</th>
                <th className="px-3 py-3">52-wk</th>
                <th className="px-3 py-3" />
              </tr>
            </thead>
            <tbody>
              {ranked.map((row) => (
                <tr key={row.company_id} className="border-b border-ink-800 last:border-0">
                  <td className="px-3 py-3 font-semibold text-mist-300">{row.rank}</td>
                  <td className="px-3 py-3">
                    <div className="flex items-center gap-2">
                      <span className="font-medium text-mist-100">{row.symbol}</span>
                      {row.instrument_type === "etf_or_fund" && (
                        <span className="rounded bg-signal-violet/15 px-1.5 py-0.5 text-[10px] font-medium uppercase text-signal-violet">
                          Fund/ETF
                        </span>
                      )}
                    </div>
                    <div className="max-w-[160px] truncate text-xs text-mist-400">{row.name}</div>
                  </td>
                  <td className="px-3 py-3 text-mist-100">{fmtPrice(row.price)}</td>
                  <td className="px-3 py-3">
                    {(() => {
                      const ret = row.outlook_1m?.expected_return_pct ?? null;
                      if (ret === null) return <span className="text-mist-500">—</span>;
                      const up = ret >= 0;
                      return (
                        <span
                          className={`inline-flex items-center gap-1 rounded px-2 py-1 text-xs font-semibold ${
                            up ? "bg-emerald-500/15 text-emerald-400" : "bg-rose-500/15 text-rose-400"
                          }`}
                        >
                          {up ? "▲ Rise" : "▼ Fall"} {fmtPct(ret)}
                        </span>
                      );
                    })()}
                  </td>
                  <td className="px-3 py-3 font-medium text-mist-100">
                    {fmtPrice(row.outlook_1m?.expected_price ?? null)}
                  </td>
                  <td className="px-3 py-3 text-mist-300">
                    {row.outlook_1m
                      ? `${fmtPrice(row.outlook_1m.bear_price)} – ${fmtPrice(row.outlook_1m.bull_price)}`
                      : "—"}
                  </td>
                  <td className="px-3 py-3 text-mist-300">
                    {row.outlook_1m ? `${(row.outlook_1m.confidence * 100).toFixed(0)}%` : "—"}
                  </td>
                  <td className="px-3 py-3 text-mist-200">{fmtRatio(row.pe_ratio)}</td>
                  <td className="px-3 py-3 text-mist-200">{fmtRatio(row.price_to_book)}</td>
                  <td className="px-3 py-3 text-mist-300">
                    {row.range_position_52w != null ? `${row.range_position_52w.toFixed(0)}%` : "—"}
                  </td>
                  <td className="px-3 py-3 text-right">
                    <Link
                      to={`/research/${row.company_id}`}
                      className="text-sm font-medium text-signal-teal hover:underline"
                    >
                      Report →
                    </Link>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {unranked.length > 0 && (
        <div>
          <p className="mb-2 text-xs uppercase tracking-wide text-mist-500">
            Loaded, not yet ranked (need ~60 days of prices)
          </p>
          <div className="flex flex-wrap gap-2">
            {unranked.map((row) => (
              <Link
                key={row.company_id}
                to={`/research/${row.company_id}`}
                className="rounded-lg border border-ink-700 bg-ink-900 px-3 py-1.5 text-sm text-mist-300 hover:border-ink-500"
              >
                {row.symbol}
                {row.instrument_type === "etf_or_fund" && (
                  <span className="ml-1 text-signal-violet">·fund</span>
                )}
                {row.price !== null && (
                  <span className="ml-1 text-mist-500">{fmtPrice(row.price)}</span>
                )}
              </Link>
            ))}
          </div>
        </div>
      )}

      {overview?.disclaimer && (
        <p className="text-xs leading-relaxed text-mist-500">{overview.disclaimer}</p>
      )}
    </div>
  );
}
